import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import type { ISearchEnginesKeywordsProps } from "app/components/React/Table/cells/SearchEnginesKeywordCell/SearchEnginesKeywordCellConst";
import { translate } from "app/filters/i18nFilter";
import {
    createListOfCampaignsWithIcons,
    getBPCampaign,
    getBPCampaignsFromServer,
    updateCampaign,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/infra/BrandMonitoringCampaignsInfraUtils";
import { BRAND_MONITORING_COMPETITORS_REPORT_STATE } from "app/pages/digital-marketing/brand-monitoring-campaigns/reports/competitors/BrandMonitoringCompetitorsReportConsts";
import type {
    IBPCampaignDataFromServer,
    IBPCampaignDataFromServerAndAssets,
    IBPCurrentCampaignData,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/BPCampaignsWizard.types";
import { getUpdatedKeywordsWithoutDuplications } from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/addingKeywordsUtils";
import type { IParsedFileData } from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/importCSVModal/importCSVModalConsts";
import { getCampaignsConsumptionFromServer } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsCampaignUtils";
import { getCampaignsFromServer } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import { getMyUserConsumptionMetrics } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsInfraUtils";
import type {
    ICampaignsConsumptionMetrics,
    IKeywordsCampaignData,
    IRTCurrentCampaignData,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { OVERVIEW_REPORT_PAGE } from "app/common/campaigns/KeywordsCampaignConsts";
import {
    getCampaign,
    updateKeywordsFromCampaign,
} from "app/pages/digital-marketing/keywords-campaigns/reports/keywords-report/keywords-table/KeywordsCamapaignKeywordsReportTableUtils";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { IAutocompleteKeywordGroup } from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import type { ISearchEngineDetails } from "../searchEngine/SearchEnginesConsts";
import { getSearchEngineAvailableLocations } from "../searchEngine/SearchEngineWizardSharedUtil";
import type {
    ICampaignDataUnion,
    ICampaignsDataUnion,
    ICampaignsScrapingConfiguration,
} from "../shared/CampaignsConst";
import { navigateToCampaign } from "../shared/CampaignsUtils";

import { ICampaignType, WarningTypes } from "./SearchTrackerFromKeywordsTableTypes";

export const fetchRankTrackersCampaignsData = async (): Promise<IKeywordsCampaignData[]> => {
    return getCampaignsFromServer()
        .then((campaignConfigs: IKeywordsCampaignData[]) => {
            return campaignConfigs;
        })
        .catch((errorFetch) => {
            Sentry.error(`Error fetching Keywords campaigns configurations from server -`, errorFetch);
            return null;
        });
};

export const fetchBrandProtectionCampaignsData = async (): Promise<IBPCampaignDataFromServer[]> => {
    const campaignsAndAssets: IBPCampaignDataFromServerAndAssets = await getBPCampaignsFromServer();

    const listOfCampaignsWithIcons = createListOfCampaignsWithIcons(
        campaignsAndAssets.assets,
        campaignsAndAssets.campaignConfigs,
    );
    return listOfCampaignsWithIcons.managedCampaigns;
};

export const getRemainingKeywordsAndCampaignsCount = async (
    campaigns: ICampaignsDataUnion,
    campaignType: ICampaignType,
) => {
    const internalUser = swSettings.user.isInternalUser;
    if (internalUser) {
        const myUser = getMyUserConsumptionMetrics(campaigns);
        return {
            usedKeywords: myUser?.usedKeywords,
            totalKeywords:
                campaignType === ICampaignType.RankTracker
                    ? swSettings.components.RankTracker.resources.MaxKeywordInternalRankTracker
                    : swSettings.components.BrandMonitor.resources.MaxKeywordInternalBrandProtection,
            usedCampaigns: myUser?.usedCampaigns,
            totalCampaigns: swSettings.components.RankTracker.resources.NumberOfCampaigns,
        };
    } else {
        return getCampaignsConsumptionFromServer({
            usedKeywords: 0,
            totalKeywords: swSettings.components.RankTracker.resources.NumberTrackedKeywords,
            usedCampaigns: 0,
            totalCampaigns: swSettings.components.RankTracker.resources.NumberOfCampaigns,
            hasMutualAssetsError: false,
        });
    }
};

const getKeywordsListByGroupId = async (keywordListId: string): Promise<IAutocompleteKeywordGroup | undefined> => {
    try {
        const groupById = await keywordGroupsQueryClient.getGroupById(keywordListId);
        return groupById as unknown as IAutocompleteKeywordGroup;
    } catch (error) {
        Sentry.error("Error in keywordGroupsQueryClient findGroupById:", error as Error);
        return undefined;
    }
};

export const getParsedDataBrandProtection = async (
    keywordListId: string,
    searchEngineStateValue: ISearchEngineDetails[],
): Promise<IParsedFileData> => {
    const keywordListFromListId = await getKeywordsListByGroupId(keywordListId);
    const availableLocations = getSearchEngineAvailableLocations(searchEngineStateValue);

    const availableSearchEngines: ISearchEnginesKeywordsProps[] = availableLocations?.map((engine) => {
        return {
            ...engine,
            isSelected: true,
        };
    });
    const selectedKeywords = keywordListFromListId?.Keywords?.map((keyword) => ({
        keyword,
        searchEngine: availableSearchEngines,
    }));
    return getUpdatedKeywordsWithoutDuplications(selectedKeywords, [], selectedKeywords.length, false);
};

export const getWarningMessage = (
    warningState: WarningTypes,
    userCampaignsDataSettings: ICampaignsConsumptionMetrics,
    selectedRowsCount: number,
    selectedCampaign: ICampaignDataUnion,
): string => {
    switch (warningState) {
        case WarningTypes.DuplicateKeywords:
            return translate("table.selection.add.keywords.to.campaign.duplicate.error.message");
        case WarningTypes.KeywordsLimitExceeded:
            return translate("table.selection.add.keywords.to.campaign.pass.limit.error.message", {
                remainingKeyword: userCampaignsDataSettings.totalKeywords - userCampaignsDataSettings.usedKeywords,
                selectedKeywords: selectedRowsCount,
                scrapingConfigurationAmount:
                    Object.keys(selectedCampaign).length !== 0 && selectedCampaign.scrapingConfigurations.length,
            });
        case WarningTypes.CampaignsLimitExceeded:
            return translate("table.selection.add.keywords.to.campaign.pass.limit.campaigns.error.message", {
                remainingCampaigns: userCampaignsDataSettings.totalCampaigns - userCampaignsDataSettings.usedCampaigns,
                scrapingConfigurationAmount:
                    Object.keys(selectedCampaign).length !== 0 && selectedCampaign.scrapingConfigurations.length,
            });
        default:
            return translate("table.selection.add.keywords.to.campaign.error.message");
    }
};

export const navigateToCampaignPage = (item: ICampaignDataUnion, isRankTrackerCampaign: boolean): void => {
    const navigateToState = isRankTrackerCampaign ? OVERVIEW_REPORT_PAGE : BRAND_MONITORING_COMPETITORS_REPORT_STATE;

    navigateToCampaign(item, navigateToState, {}, !isRankTrackerCampaign);
};

export const translateSuccessMessage = (
    amountOfKeywords: number,
    campaignName: string,
    campaignType: ICampaignType,
): string => {
    return translate("table.selection.add.keywords.to.campaign.success.message", {
        amountOfKeywords,
        campaignName,
        campaignType,
    });
};

export const updateCampaignWithKeywords = async (
    item: ICampaignDataUnion,
    keywordData,
    isRankTrackerCampaign: boolean,
): Promise<void> => {
    const { id, name, site } = item;

    if (isRankTrackerCampaign) {
        await updateKeywordsFromCampaign({ keywordChanges: keywordData }, id);
    } else {
        const { assetRolesSettings = { userAssetRoles: [] } } = item as IBPCampaignDataFromServer;
        await updateCampaign(
            {
                removedScrapingConfigurationGuids: [],
                emailConfigurations: [],
                keywordChanges: keywordData,
                name,
                site,
                assetRolesSettings,
            },
            id,
        );
    }
};

export const fetchSelectedCampaignData = async (
    item: ICampaignDataUnion,
    isRankTrackerCampaign: boolean,
): Promise<IRTCurrentCampaignData | IBPCurrentCampaignData> => {
    return isRankTrackerCampaign ? getCampaign(item.id, [], false) : getBPCampaign(item.id, []);
};

export const getUniqKeywords = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    selectedRows: string[],
): string[] => {
    const mergedKeywordsSet = new Set<string>();

    scrapingConfigurations.forEach((scraping) => {
        scraping.keywords.forEach((keyword) => {
            mergedKeywordsSet.add(keyword);
        });
    });

    const uniqueKeywords: string[] = selectedRows.filter((keyword) => !mergedKeywordsSet.has(keyword));

    return uniqueKeywords;
};
